import { VStack } from "@chakra-ui/react";
import type {
  CampaignIntent,
  DisplayMarketingCampaign,
} from "common/interfaces/KYDMarketing";
import type {
  FacebookPage,
  InstagramAccount,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/types";
import AdSetCard from "./components/AdSetCard";

export default function AdSetCards({
  campaign,
  pages,
  instagramAccounts,
  onEditAdset,
  onEditAd,
  onPauseCampaign,
  onPublishCampaign,
  onUnpublishCampaign,
  onPublishAdset,
  onPublishAd,
  onUnpublishAdset,
  onUnpublishAd,
  isPublishingCampaign,
  activatingAdKey,
  pausingAdsetId,
  publishingAdsetId,
  pausingAdKey,
  onRefetch,
  onApplyIntent,
  onIgnoreIntent,
  applyingIntentId,
  ignoringIntentId,
  onOpenWizardReview,
  registerCardRef,
}: {
  campaign: DisplayMarketingCampaign;
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  onEditCampaign: () => void;
  onEditAdset: (adsetId: string) => void;
  /** Open the wizard on the ad creative form for the given ad (advanced flow). */
  onEditAd?: (
    ad: import("@common/interfaces/KYDMarketing").DisplayMarketingAd
  ) => void;
  onPauseCampaign: () => void;
  onPublishCampaign: (p: { id: string }) => void;
  onUnpublishCampaign: (p: { id: string }) => void;
  onPublishAdset: (p: { campaign_id: string; adset_id: string }) => void;
  onPublishAd?: (p: {
    campaign_id: string;
    adset_id: string;
    ad_id: string;
  }) => void | Promise<void>;
  onUnpublishAdset: (p: { campaign_id: string; adset_id: string }) => void;
  onUnpublishAd: (p: {
    campaign_id: string;
    adset_id: string;
    ad_id: string;
  }) => void | Promise<void>;
  isPublishingCampaign?: boolean;
  activatingAdKey?: string | null;
  pausingAdsetId?: string | null;
  publishingAdsetId?: string | null;
  pausingAdKey?: string | null;
  onRefetch: () => void;
  onApplyIntent?: (intent: CampaignIntent) => void | Promise<void>;
  onIgnoreIntent?: (intent: CampaignIntent) => void | Promise<void>;
  /** Intent id currently being applied (show loading on that link). */
  applyingIntentId?: string | null;
  /** Intent id currently being ignored (show "Ignoring…" on that link). */
  ignoringIntentId?: string | null;
  /** Open the ads manager wizard on the review step for the given ad set (e.g. for "View" on frequency). */
  onOpenWizardReview?: (adsetId: string) => void;
  /** Registers each card's DOM node by adset id for scroll-into-view. */
  registerCardRef?: (adsetId: string, el: HTMLElement | null) => void;
}) {
  const campaignIntents = campaign.intents ?? [];
  const adsets = campaign.adsets ?? [];

  return (
    <VStack align="stretch" spacing={5}>
      {adsets.map((adset) => {
        const intentsForAdset =
          adset.intents?.length != null && adset.intents.length > 0
            ? adset.intents
            : campaignIntents.filter((i) =>
                i.actions?.some(
                  (a) => a.entity_type === "adset" && a.entity_id === adset.id
                )
              );
        return (
          <AdSetCard
            key={adset.id}
            adset={adset}
            campaign={campaign}
            pages={pages}
            instagramAccounts={instagramAccounts}
            intentsForAdset={intentsForAdset}
            intents={campaignIntents}
            onEditAdset={onEditAdset}
            onEditAd={onEditAd}
            onPublishAdset={onPublishAdset}
            onPublishAd={onPublishAd}
            onUnpublishAdset={onUnpublishAdset}
            onUnpublishAd={onUnpublishAd}
            isPublishingCampaign={isPublishingCampaign}
            activatingAdKey={activatingAdKey}
            pausingAdsetId={pausingAdsetId}
            publishingAdsetId={publishingAdsetId}
            pausingAdKey={pausingAdKey}
            onRefetch={onRefetch}
            onApplyIntent={onApplyIntent}
            onIgnoreIntent={onIgnoreIntent}
            applyingIntentId={applyingIntentId}
            ignoringIntentId={ignoringIntentId}
            onOpenWizardReview={onOpenWizardReview}
            refCallback={
              registerCardRef
                ? (el) => registerCardRef(adset.id, el)
                : undefined
            }
          />
        );
      })}
    </VStack>
  );
}
